import { z } from "zod";

import { fetchWithError, useParsedQueries, useParsedQuery } from "@nens/react-query-utils";
import { combineUrlAndParams } from "@nens/shared-utils";

import { lineStringSchema } from "../geojson";
import { paginatedSchema } from "../pagination";

import { baseAssetSchema } from "./assetsBase";

export const pipeListItemSchema = baseAssetSchema.extend({
  geometry: lineStringSchema,
  start_point: z.string().nullable() /* URL to Manhole */,
  end_point: z.string().nullable() /* URL to Manhole */,
  connection_serial: z.number().nullable(),
  invert_level_start_point: z.number().nullable(),
  invert_level_end_point: z.number().nullable(),
  length: z.number().nullable(),
  type: z.string(),
  material: z.string(),
  width: z.number().nullable(),
  height: z.number().nullable(),
  shape: z.string(),
  number_of_inhabitants: z.number().nullable(),
  dwa_definition: z.string(),
  allowed_flow_direction: z.string().nullable(),
});

export type PipeListItem = z.infer<typeof pipeListItemSchema>;

export const pipeListSchema = paginatedSchema(pipeListItemSchema);
export type PipeList = z.infer<typeof pipeListSchema>;

export const pipeSchema = pipeListItemSchema;
export type Pipe = z.infer<typeof pipeSchema>;

export function usePipes(params = {}) {
  const url = combineUrlAndParams("/api/v4/pipes/", params);
  return useParsedQuery<PipeList>(
    ["lizard-api-v4", "pipes", url],
    () => fetchWithError(url),
    (json: unknown) => pipeListSchema.parse(json)
  );
}

export function usePipe(id: number) {
  return useParsedQuery<Pipe>(
    ["lizard-api-v4", "pipes", id],
    () => fetchWithError(`/api/v4/pipes/${id}/`),
    (json: unknown) => pipeSchema.parse(json)
  );
}

export function useMultiplePipes(ids: number[]) {
  return useParsedQueries(
    ids.map((id) => {
      return {
        queryKey: ["lizard-api-v4", "pipes", id],
        fetchFunction: () => fetchWithError(`/api/v4/pipes/${id}/`),
        parseFunction: (json: unknown) => pipeSchema.parse(json),
      };
    })
  );
}
